import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { TranslationKey } from './types';
import enMessages from './langs/en.json';
import ptBRMessages from './langs/pt-BR.json';
import esMessages from './langs/es.json';

/**
 * Supported langs in the application.
 */
export type Locale = 'en' | 'pt-BR' | 'es';

/**
 * Dictionary type mapping translation keys to strings.
 */
type Messages = Record<TranslationKey, string>;

/**
 * Mapping of locale identifiers to their respective message dictionaries.
 */
const localeMap: Record<Locale, Messages> = {
  en: enMessages,
  'pt-BR': ptBRMessages,
  es: esMessages,
};

const STORAGE_KEY = 'cb-locale';

/**
 * Detects the initial locale based on localStorage or browser settings.
 * Defaults to 'en' if no match is found.
 */
function detectLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (saved && localeMap[saved]) return saved;
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('pt')) return 'pt-BR';
  return 'en';
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Provider component for internationalization context.
 * Manages the current locale and provides the translation function 't'.
 */
export function I18nProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [localeState, setLocaleState] = useState<Locale>(detectLocale);

  /**
   * Updates the current locale and persists it to localStorage.
   */
  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem(STORAGE_KEY, newLocale);
  }, []);

  /**
   * Translation function 't'.
   * Retrieves the message for a given key in the current locale.
   * Supports variable interpolation using {key} syntax.
   */
  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>): string => {
      const messages = localeMap[localeState];
      const fallback = localeMap['en'];
      let value = messages[key] ?? fallback[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          value = value.replace(`{${k}}`, String(v));
        });
      }
      return value;
    },
    [localeState]
  );

  const value = useMemo(() => ({ locale: localeState, setLocale, t }), [localeState, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Custom hook to access i18n context values.
 * Must be used within an I18nProvider.
 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
